import { NgTemplateOutlet } from '@angular/common';
import { Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent } from '@app/components/platform';
import { ShowIfFeatureFlagTrueDirective } from '@app/directives/feature-flag/show-if-feature-flag';
import { PersonaInquiry } from '@app/models/kyc-kyb/persona-inquiry.model';
import { AuthService, NotifyService } from '@app/services';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { remove } from 'lodash-es';
import { KYCKYBBadge } from '../kyc-kyb-badge/kyc-kyb-badge.component';

/**
 * Displays a table of KYB inquiry info
 */
@Component({
    templateUrl: 'kyb-inquiries.template.html',
    selector: 'app-kyb-inquiries',
    standalone: true,
    styleUrls: ['./kyb-inquiries.style.scss'],
    imports: [
        KYCKYBBadge,
        MatIconModule,
        NgTemplateOutlet,
        TranslateModule,
        ButtonComponent,
        ShowIfFeatureFlagTrueDirective,
    ],
})
export class KYBInquiriesTable {
    /**
     * if the user currently viewing the table is the same one that initiated KYB
     */
    isCurrentUserInitiator = computed(
        () => this.companyInquiry()?.initiatorEmployeeId === this.authService.employee.id
    );
    /**
     * If the user currently viewing the table is the same one that was indicated as the signing officer in the KYB form
     */
    isCurrentUserSigningOfficer = computed(() => this.companyInquiry()?.email === this.authService.account.email);

    /**
     * The parent inquiry the entire table is based on. If the user is not the initiator or signing officer the table will not display
     */
    inquiry = computed(() => {
        if (this.isCurrentUserInitiator() || this.isCurrentUserSigningOfficer()) {
            return this.companyInquiry();
        }
        return undefined;
    });

    /**
     * The KYC account associated with the current user
     */
    account = toSignal(this.knowYourService.personaAccount);

    /**
     * Retrieves the inquiry data specific to the user indicated as the signing officer
     */
    signingOfficer = computed(() => {
        const inquiry = this.inquiry();
        if (!inquiry) {
            return undefined;
        }

        if (this.isCurrentUserSigningOfficer()) {
            // Try to pull the specific UBO that is associated with the account viewing the table. They will only have a UBO entry if they > 10% ownership
            const officer = remove(
                inquiry.children,
                ({ inquiryId }) => inquiryId === this.account()?.inquiry?.inquiryId
            )[0];

            if (officer) {
                return officer;
            } else {
                // If there is no matching UBO record, they have < 10% ownership, pull the name data from the parent inquiry
                return {
                    firstName: inquiry.firstName,
                    lastName: inquiry.lastName,
                    email: inquiry.email,
                };
            }
        }
        // If the current user is NOT the signing officer, they must be the initiator in order to reach this point

        // Try to pull the specific UBO that is associated with the parent inquiry based on email address. They will only have a UBO entry if they > 10% ownership
        const officer = remove(inquiry.children, ({ email }) => email === inquiry.email)[0];

        if (officer) {
            return officer;
        } else {
            // If there is no matching UBO record, they have < 10% ownership, pull the name data from the parent inquiry
            return {
                firstName: inquiry.firstName,
                lastName: inquiry.lastName,
                email: inquiry.email,
            };
        }
    });

    /**
     * Universal Beneficial Owners for the company, as entered by the signing officer during KYB verification
     */
    UBOs = computed(() => {
        const inquiry = this.inquiry();

        if (!inquiry) {
            return [];
        }
        return inquiry.children;
    });

    /**
     * A record of sent invites, used to disable the button after sending an invite to avoid spam-clicking
     */
    sentInvites: Record<string, boolean> = {};

    /**
     * The inquiry data retrieved from the backend via the current KYB's inquiry ID
     */
    private companyInquiry = toSignal(this.knowYourService.companyInquiry);

    constructor(
        private knowYourService: KYCKYBService,
        private authService: AuthService,
        private notifyService: NotifyService
    ) {}

    resendInvite(inquiry: PersonaInquiry): void {
        this.sentInvites[inquiry.id] = false; // false = loading, button is no longer displayed
        this.knowYourService.resendInvite(inquiry).subscribe({
            next: () => {
                this.notifyService.success({
                    key: 'payroll.kyc-kyb.payroll-setup.business-verification.sendSuccess',
                    params: { name: `${inquiry.firstName} ${inquiry.lastName}` },
                });
                this.sentInvites[inquiry.id] = true; // true = sent, button is no longer displayed
            },
            error: (e) => {
                Sentry.captureException(e);
                this.notifyService.error({
                    key: 'payroll.kyc-kyb.payroll-setup.business-verification.sendError',
                    params: { name: `${inquiry.firstName} ${inquiry.lastName}` },
                });
                delete this.sentInvites[inquiry.id]; // undefined = button re-appears and is able to be clicked
            },
        });
    }
}
